import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    const r = await register(email.trim(), password, name.trim());
    setLoading(false);
    if (!r.ok) setError(r.error);
    else navigate("/quiz");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2" data-testid="register-page">
      <aside className="bg-[var(--es-blue)] text-white relative overflow-hidden hidden md:flex flex-col justify-between p-12">
        <Link to="/" className="inline-flex items-center gap-2 z-10">
          <Logo size={32} variant="mark" />
          <span className="font-display text-xl">Erudition <span className="italic">Score</span></span>
        </Link>
        <div className="z-10">
          <h1 className="font-display text-5xl leading-[1.05]">
            Begin your <br />
            <em className="text-white/80">first reading.</em>
          </h1>
          <p className="font-body text-white/70 mt-6 max-w-md">
            Create an account to save your Erudition Score, track your growth across the five
            pillars, and unlock a personalised AI growth plan.
          </p>
        </div>
        <div className="absolute -right-32 -bottom-32 opacity-10">
          <Logo size={620} variant="mark" />
        </div>
      </aside>

      <main className="flex items-center justify-center p-8 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md fade-in" data-testid="register-form">
          <p className="font-ui text-xs uppercase tracking-[0.28em] text-[var(--es-blue)]">Create account</p>
          <h2 className="font-display text-4xl text-[var(--es-ink)] mt-2">Become measurable.</h2>
          <p className="font-body text-[var(--es-ink-soft)] mt-2">
            Already have an account?{" "}
            <Link to="/login" className="text-[var(--es-blue)] underline" data-testid="register-to-login">
              Sign in
            </Link>
            .
          </p>

          <div className="mt-10 space-y-6">
            <div>
              <label className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)]">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full border-b border-[var(--es-line)] focus:border-[var(--es-blue)] outline-none py-3 bg-transparent font-body"
                data-testid="register-name-input"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)]">Email</label>
              <input
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full border-b border-[var(--es-line)] focus:border-[var(--es-blue)] outline-none py-3 bg-transparent font-body"
                data-testid="register-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)]">
                Password (min 6)
              </label>
              <input
                type="password"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full border-b border-[var(--es-line)] focus:border-[var(--es-blue)] outline-none py-3 bg-transparent font-body"
                data-testid="register-password-input"
                autoComplete="new-password"
              />
            </div>
          </div>

          {error && (
            <p className="mt-6 text-sm text-red-600 font-body" data-testid="register-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-[var(--es-blue)] text-white py-4 hover:bg-[var(--es-blue-hover)] disabled:opacity-60 transition-colors uppercase tracking-wider text-sm font-semibold font-ui"
            data-testid="register-submit-button"
          >
            {loading ? "Creating account…" : "Create account & begin"}
          </button>
        </form>
      </main>
    </div>
  );
}
